import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Icon } from '../components/Icon.js';
import { NewsPage } from './NewsPage.js';
import { TeamPage } from './TeamPage.js';

// "הגדרות" — one hub. Sub-nav sections: הגדרות (channels/forwarding/alerts) · פרסום אוטומטי (news
// autopilot) · משתמשים (team, managers only). מדריך stays a quiet help link, not top-level nav.
// Merges the former /news + /team (which redirect here) into sections.

type Section = 'general' | 'news' | 'team';

// "הגדרות פרסום" (Phase 5, items 1/4/5). Telegram connect wizard + inbound-forward + alerts.
// The Telegram token is entered here but NEVER read back — the API returns only a connected flag
// and the public @username (Clean Architecture: no secret in the browser).

const cardStyle: React.CSSProperties = { padding: 24, marginBottom: 20 };
const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' };
const labelStyle: React.CSSProperties = { fontSize: 13, color: 'var(--text-dim)', marginBottom: 8, fontWeight: 600 };
const inputStyle: React.CSSProperties = {
  height: 42, padding: '0 14px', borderRadius: 'var(--pill)', border: '1px solid var(--hair)',
  background: 'var(--surface-2)', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: 14, minWidth: 260,
};
const selectStyle: React.CSSProperties = { ...inputStyle, minWidth: 240 };

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }): JSX.Element {
  return (
    <button
      onClick={onClick}
      aria-pressed={on}
      style={{
        width: 46, height: 26, borderRadius: 'var(--pill)', border: '1px solid var(--hair)', cursor: 'pointer',
        background: on ? 'var(--teal-800)' : 'var(--surface-1)', position: 'relative', transition: 'background .2s', flex: '0 0 auto',
      }}
    >
      <span style={{
        position: 'absolute', top: 2, insetInlineStart: on ? 22 : 2, width: 20, height: 20, borderRadius: '50%',
        background: '#F3EFE4', transition: 'inset-inline-start .2s',
      }} />
    </button>
  );
}

function GeneralSettings(): JSX.Element {
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const [articleUrl, setArticleUrl] = useState('');
  const [articleText, setArticleText] = useState<string | null>(null);

  const flash = (t: string): void => { setToast(t); setTimeout(() => setToast(null), 2600); };
  const invalidate = (): void => void qc.invalidateQueries({ queryKey: ['settings'] });

  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: () => api.settings() });
  const { data: targets } = useQuery({ queryKey: ['targets'], queryFn: () => api.targets() });
  const groups = (targets ?? []).filter((t) => t.surface === 'wa_group');

  const connect = useMutation({
    mutationFn: () => api.connectTelegram(token),
    onSuccess: (r) => { setToken(''); invalidate(); flash(`בוט טלגרם חובר: @${r.botUsername}`); },
    onError: () => flash('טוקן טלגרם לא תקין'),
  });
  const disconnect = useMutation({
    mutationFn: () => api.disconnectTelegram(),
    onSuccess: () => { invalidate(); flash('בוט טלגרם נותק'); },
  });
  const update = useMutation({
    mutationFn: api.updateSettings,
    onSuccess: () => { invalidate(); flash('ההגדרות נשמרו'); },
    onError: () => flash('שמירה נכשלה'),
  });
  const extract = useMutation({
    mutationFn: () => api.article(articleUrl),
    onSuccess: (r) => setArticleText(`${r.title}\n\n${r.text}`.slice(0, 4000)),
    onError: () => flash('שליפת הכתבה נכשלה'),
  });

  const tg = settings?.telegram;
  const inbound = settings?.inboundForward;
  const alerts = settings?.alerts;

  // "מילת השידור" — the owner-configurable keyword that triggers a broadcast when typed at the start of
  // a message in the control group FROM THE PHONE (single-number path). Auto-saves on blur (like the
  // other settings). Seeded once from the real saved value; empty/unchanged is not saved.
  const [keyword, setKeyword] = useState<string | null>(null);
  useEffect(() => {
    if (settings && keyword === null) setKeyword(settings.broadcastKeyword);
  }, [settings, keyword]);
  const saveKeyword = (): void => {
    const k = (keyword ?? '').trim();
    if (!k) { setKeyword(settings?.broadcastKeyword ?? 'שידור'); return; } // never save empty — revert
    if (k === settings?.broadcastKeyword) return;
    update.mutate({ broadcastKeyword: k });
  };

  // "מתי לשלוח?" send-window control — same api.updateSettings mutation. Auto-saves on change like the
  // toggles above. Immediate = 0/24 (never held); quiet-hours = the [start,end) the user picks.
  const [swMode, setSwMode] = useState<'immediate' | 'quiet' | null>(null);
  const [swStart, setSwStart] = useState(8);
  const [swEnd, setSwEnd] = useState(22);
  useEffect(() => {
    if (!settings || swMode !== null) return;
    const { start, end } = settings.sendWindow;
    if (start === 0 && end === 24) setSwMode('immediate');
    else { setSwMode('quiet'); setSwStart(start); setSwEnd(end); }
  }, [settings, swMode]);
  const saveWindow = (w: { start: number; end: number }): void => update.mutate({ sendWindow: w });
  const pickImmediate = (): void => { setSwMode('immediate'); saveWindow({ start: 0, end: 24 }); };
  const pickQuiet = (): void => { setSwMode('quiet'); saveWindow({ start: swStart, end: swEnd }); };
  const changeSwStart = (v: number): void => {
    const e = swEnd <= v ? Math.min(v + 1, 24) : swEnd;
    setSwStart(v); setSwEnd(e); saveWindow({ start: v, end: e });
  };
  const changeSwEnd = (v: number): void => { setSwEnd(v); saveWindow({ start: swStart, end: v }); };
  const hourLabel = (h: number): string => `${String(h).padStart(2, '0')}:00`;
  const swStartOpts = Array.from({ length: 24 }, (_, i) => i);
  const swEndOpts = Array.from({ length: 24 - swStart }, (_, i) => swStart + 1 + i);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">הגדרות</div>
          <h1>הגדרות</h1>
          <div className="sub">ערוצים נוספים, העברת הודעות פרטיות והתראות</div>
        </div>
      </div>

      {/* "מתי לשלוח?" — user-configurable send timing (immediate vs quiet-hours). */}
      <div className="card" style={cardStyle}>
        <h3 style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-display)', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Icon name="clock" /> מתי לשלוח הודעות
        </h3>
        <div style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 14 }}>
          אתם מחליטים מתי הודעה יוצאת לקבוצות. אפשר לשלוח מיד, או רק בשעות שתבחרו — הודעה שנכתבה מחוץ לשעות תמתין ותישלח בבוקר.
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: swMode === 'quiet' ? 16 : 0 }}>
          <button
            type="button"
            className={swMode === 'immediate' ? 'btn' : 'btn ghost'}
            onClick={pickImmediate}
          >
            ⚡ שליחה מיידית
          </button>
          <button
            type="button"
            className={swMode === 'quiet' ? 'btn' : 'btn ghost'}
            onClick={pickQuiet}
          >
            🌙 רק בשעות שאבחר
          </button>
        </div>
        {swMode === 'quiet' && (
          <div>
            <div style={{ ...rowStyle, fontSize: 15 }}>
              <span>שלחו בין השעה</span>
              <select style={{ ...selectStyle, minWidth: 110 }} value={swStart} onChange={(e) => changeSwStart(Number(e.target.value))}>
                {swStartOpts.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
              </select>
              <span>לשעה</span>
              <select style={{ ...selectStyle, minWidth: 110 }} value={swEnd} onChange={(e) => changeSwEnd(Number(e.target.value))}>
                {swEndOpts.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
              </select>
            </div>
            <div style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 10 }}>
              הודעה שתישלח אחרי {hourLabel(swEnd)} תמתין ותצא למחרת בשעה {hourLabel(swStart)}.
            </div>
          </div>
        )}
      </div>

      {/* "מילת השידור" — custom broadcast keyword for the single-number (phone) path. */}
      <div className="card" style={cardStyle}>
        <h3 style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-display)', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Icon name="send" /> מילת השידור
        </h3>
        <div style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 14 }}>
          כשתכתוב מילה זו בתחילת הודעה בקבוצת השליטה מהטלפון — נשדר אותה לכל קבוצות היעד. שאר ההודעה (מהשורה הבאה) היא התוכן שיישלח. הודעה רגילה בלי המילה הזו לא תשודר.
        </div>
        <div style={labelStyle}>המילה שמפעילה שידור</div>
        <input
          style={{ ...inputStyle, minWidth: 220 }}
          value={keyword ?? ''}
          onChange={(e) => setKeyword(e.target.value)}
          onBlur={saveKeyword}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          placeholder="שידור"
          maxLength={40}
        />
      </div>

      {/* Telegram connect wizard */}
      <div className="card" style={cardStyle}>
        <div style={{ ...rowStyle, justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-display)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon name="telegram" /> Telegram
          </h3>
          {tg?.connected
            ? <span className="chip green">מחובר · @{tg.botUsername}</span>
            : <span className="chip muted">לא מחובר</span>}
        </div>
        {tg?.connected ? (
          <div style={rowStyle}>
            <div style={{ color: 'var(--text-dim)', fontSize: 14 }}>הבוט מחובר. את ערוצי Telegram בוחרים בעמוד "המספרים והקבוצות" (לשונית קבוצות).</div>
            <button className="btn ghost" style={{ marginInlineStart: 'auto' }} onClick={() => disconnect.mutate()} disabled={disconnect.isPending}>
              <Icon name="close" /> נתק
            </button>
          </div>
        ) : (
          <div>
            <div style={labelStyle}>טוקן בוט (מ-BotFather) — נשמר מוצפן בשרת, לא נחשף בדפדפן</div>
            <div style={rowStyle}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                type="password"
                dir="ltr"
                placeholder="123456:ABC-DEF…"
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
              <button className="btn" onClick={() => connect.mutate()} disabled={connect.isPending || token.length < 20}>
                <Icon name="check" /> {connect.isPending ? 'מאמת…' : 'חבר ואמת'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Inbound forward */}
      <div className="card" style={cardStyle}>
        <div style={{ ...rowStyle, justifyContent: 'space-between', marginBottom: 6 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-display)' }}>העברת הודעות פרטיות</h3>
          <Toggle on={Boolean(inbound?.enabled)} onClick={() => update.mutate({ inboundForward: { enabled: !inbound?.enabled } })} />
        </div>
        <div style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 14 }}>
          כשמישהו שולח הודעה פרטית למספר שלך — היא תועבר אוטומטית לקבוצה שתבחר (עם המספר של השולח והתוכן), כדי שלא תפספס פניות. השאר על "ללא" אם אינך צריך.
        </div>
        <div style={labelStyle}>לאן להעביר את ההודעות</div>
        <select
          style={selectStyle}
          value={inbound?.targetId ?? ''}
          onChange={(e) => update.mutate({ inboundForward: { targetId: e.target.value || null } })}
        >
          <option value="">— ללא —</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>

      {/* Alerts */}
      <div className="card" style={cardStyle}>
        <div style={{ ...rowStyle, justifyContent: 'space-between', marginBottom: 6 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-display)' }}>התראות 🚨</h3>
          <Toggle on={Boolean(alerts?.enabled)} onClick={() => update.mutate({ alerts: { enabled: !alerts?.enabled } })} />
        </div>
        <div style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 14 }}>
          התראות דחופות (עלייה בכשלונות, מספר שהושהה) יישלחו לקבוצה שתבחרו.
        </div>
        <div style={labelStyle}>קבוצת התראות</div>
        <select
          style={selectStyle}
          value={alerts?.targetId ?? ''}
          onChange={(e) => update.mutate({ alerts: { targetId: e.target.value || null } })}
        >
          <option value="">— ללא —</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>

      {/* כתבה! extractor */}
      <div className="card" style={cardStyle}>
        <h3 style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-display)', marginBottom: 6 }}>שליפת טקסט מכתבה</h3>
        <div style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 14 }}>הדבק קישור לכתבה וקבל את הטקסט המלא שלה.</div>
        <div style={rowStyle}>
          <input style={{ ...inputStyle, flex: 1 }} dir="ltr" placeholder="https://…" value={articleUrl} onChange={(e) => setArticleUrl(e.target.value)} />
          <button className="btn" onClick={() => extract.mutate()} disabled={extract.isPending || !articleUrl}>
            <Icon name="download" /> {extract.isPending ? 'שולף…' : 'שלוף'}
          </button>
        </div>
        {articleText && (
          <textarea
            readOnly
            dir="rtl"
            value={articleText}
            style={{ marginTop: 14, width: '100%', minHeight: 200, padding: 14, borderRadius: 14, border: '1px solid var(--hair)', background: 'var(--surface-1)', color: 'var(--text)', fontSize: 14, fontFamily: 'var(--font-body)', resize: 'vertical' }}
          />
        )}
      </div>

      {toast && <div className="toast"><span className="d" /> {toast}</div>}
    </>
  );
}

const SECTIONS: { key: Section; label: string; managerOnly?: boolean }[] = [
  { key: 'general', label: 'הגדרות' },
  { key: 'news', label: 'פרסום אוטומטי' },
  { key: 'team', label: 'משתמשים', managerOnly: true },
];

export function SettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const { section: raw } = useParams();
  const section: Section = raw === 'news' || raw === 'team' ? raw : 'general';

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const isManager = me?.user.role === 'owner' || me?.user.role === 'admin';

  return (
    <div className="content">
      <div className="filter-tabs" style={{ marginBottom: 20, alignItems: 'center' }}>
        {SECTIONS.filter((s) => !s.managerOnly || isManager).map((s) => (
          <button
            key={s.key}
            className={section === s.key ? 'on' : ''}
            onClick={() => navigate(s.key === 'general' ? '/settings' : `/settings/${s.key}`)}
          >
            {s.label}
          </button>
        ))}
        <Link to="/guide" className="foot-link" style={{ marginInlineStart: 'auto', color: 'var(--text-dim)', fontSize: 13 }}>
          <Icon name="guide" /> מדריך
        </Link>
      </div>

      {section === 'general' && <GeneralSettings />}
      {section === 'news' && <NewsPage />}
      {section === 'team' && <TeamPage />}
    </div>
  );
}
